import { useEffect, useMemo, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Row from 'react-bootstrap/Row';
import { ConfigModalMode, LoyaltyCampaignOfferOptionRow } from '../types';
import { formatOfferOptionLabel, parseSkus } from '../utils';

type ConfigModalProps = {
  show: boolean;
  configModalMode: ConfigModalMode;
  configGroupAmount: number | null;
  configAmount: string;
  bulkSkusText: string;
  selectedSku: string;
  offerOptions: LoyaltyCampaignOfferOptionRow[];
  offerSkuMap: Record<string, string[]>;
  hasValidConfigAmount: boolean;
  upsertConfigLoading: boolean;
  onHide: () => void;
  onSave: () => void;
  setConfigAmount: (value: string) => void;
  setBulkSkusText: (value: string) => void;
  setSelectedSku: (value: string) => void;
};

const ConfigModal = ({
  show,
  configModalMode,
  configGroupAmount,
  configAmount,
  bulkSkusText,
  selectedSku,
  offerOptions,
  offerSkuMap,
  hasValidConfigAmount,
  upsertConfigLoading,
  onHide,
  onSave,
  setConfigAmount,
  setBulkSkusText,
  setSelectedSku,
}: ConfigModalProps) => {
  const [viewportHeight, setViewportHeight] = useState<number>(
    typeof window === 'undefined' ? 900 : window.innerHeight
  );
  const mappedSkus = selectedSku ? offerSkuMap[selectedSku] ?? [] : [];
  const selectedSkuList = useMemo(() => parseSkus(bulkSkusText), [bulkSkusText]);
  const selectedSkuSet = useMemo(() => new Set(selectedSkuList), [selectedSkuList]);
  const selectedOffers = useMemo(
    () =>
      offerOptions.filter((offer) => {
        const mappedOfferSkus = offerSkuMap[offer.id] ?? offer.skus ?? [];
        return mappedOfferSkus.some((sku) => selectedSkuSet.has(sku));
      }),
    [offerOptions, offerSkuMap, selectedSkuSet]
  );
  const unmatchedManualSkus = useMemo(() => {
    const mappedSkuUniverse = new Set(
      offerOptions.flatMap((offer) =>
        (offerSkuMap[offer.id] ?? offer.skus ?? []).map((sku) => sku.trim()).filter(Boolean)
      )
    );
    return selectedSkuList.filter((sku) => !mappedSkuUniverse.has(sku));
  }, [offerOptions, offerSkuMap, selectedSkuList]);
  const isCompact = viewportHeight < 860;

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleResize = () => setViewportHeight(window.innerHeight);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const addSelectedOffer = (offerId: string) => {
    if (!offerId) {
      setSelectedSku('');
      return;
    }

    const existing = parseSkus(bulkSkusText);
    const mappedOfferSkus = offerSkuMap[offerId] ?? [offerId];
    const nextSkus = mappedOfferSkus.filter((sku) => !existing.includes(sku));
    if (nextSkus.length > 0) {
      setBulkSkusText(existing.concat(nextSkus).join('\n'));
    }
    setSelectedSku('');
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton style={{ padding: isCompact ? '12px 16px' : undefined }}>
        <Modal.Title>
          {configModalMode === 'groupAdd'
            ? `Add SKU to ${configGroupAmount} Loyalty Points`
            : configModalMode === 'groupEdit'
              ? 'Edit Group Points'
              : 'Create Loyalty Config'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ padding: isCompact ? 16 : undefined }}>
        <Row className={`${isCompact ? 'g-1' : 'g-2'} align-items-end`}>
          <Col md={2}>
            <Form.Label className="mb-1">Loyalty Points</Form.Label>
            <Form.Control
              placeholder="Points"
              type="number"
              value={configAmount}
              disabled={configModalMode === 'groupAdd'}
              onChange={(e) => setConfigAmount(e.target.value)}
            />
          </Col>
          <Col md={8}>
            <Form.Label className="mb-1">Offer Picker</Form.Label>
            <Form.Control
              as="select"
              value={selectedSku}
              disabled={configModalMode === 'groupEdit'}
              onChange={(e) => {
                const nextValue = e.currentTarget.value;
                setSelectedSku(nextValue);
                if (nextValue) {
                  addSelectedOffer(nextValue);
                }
              }}
            >
              <option value="">Select Offer</option>
              {offerOptions.map((offer) => (
                <option key={offer.id} value={offer.id}>
                  {formatOfferOptionLabel(offer)}
                </option>
              ))}
            </Form.Control>
            {selectedSku ? (
              <div
                className="mt-2"
                style={{ padding: '10px 12px', borderRadius: 12, background: '#f8fafc', border: '1px solid #e2e8f0' }}
              >
                <div className="small font-weight-bold text-uppercase text-muted mb-1">Mapped SKU Detail</div>
                {mappedSkus.length > 0 ? (
                  mappedSkus.map((sku) => (
                    <div key={sku} className="small text-monospace" style={{ wordBreak: 'break-all' }}>
                      {sku}
                    </div>
                  ))
                ) : (
                  <div className="small text-muted">No SKU mapping available for this offer.</div>
                )}
              </div>
            ) : null}
          </Col>
          <Col md={2} className="d-flex">
            <Button
              className="w-auto"
              variant="success"
              style={{
                whiteSpace: 'nowrap',
                height: 38,
                minWidth: 92,
                padding: '0 12px',
                backgroundColor: '#22b573',
                borderColor: '#22b573',
              }}
              disabled={!selectedSku || configModalMode === 'groupEdit'}
              onClick={() => addSelectedOffer(selectedSku)}
            >
              Add Offer
            </Button>
          </Col>
        </Row>

        <Row className={`${isCompact ? 'g-1' : 'g-2'} mt-2`}>
          <Col>
            <Form.Label className="mb-1">Selected Offers</Form.Label>
            <div
              style={{
                padding: isCompact ? '10px 12px' : '12px 14px',
                borderRadius: 12,
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                minHeight: isCompact ? 88 : 104,
              }}
            >
              {selectedOffers.length > 0 ? (
                <div className="d-flex flex-column" style={{ gap: 10 }}>
                  {selectedOffers.map((offer) => {
                    const mappedOfferSkus = offerSkuMap[offer.id] ?? offer.skus ?? [];
                    return (
                      <div
                        key={offer.id}
                        style={{
                          borderRadius: 12,
                          background: '#ffffff',
                          border: '1px solid #edf1f5',
                          padding: '10px 12px',
                        }}
                      >
                        <div className="d-flex justify-content-between align-items-start" style={{ gap: 12 }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              className="small font-weight-bold"
                              style={{
                                color: '#172033',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                              title={formatOfferOptionLabel(offer)}
                            >
                              {formatOfferOptionLabel(offer)}
                            </div>
                            <div className="small text-muted" style={{ marginTop: 4 }}>
                              {mappedOfferSkus.map((sku) => (
                                <div key={sku} className="text-monospace" style={{ wordBreak: 'break-all' }}>
                                  {sku}
                                </div>
                              ))}
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            disabled={configModalMode === 'groupEdit'}
                            onClick={() => {
                              const remainingSkus = selectedSkuList.filter((sku) => !mappedOfferSkus.includes(sku));
                              setBulkSkusText(remainingSkus.join('\n'));
                              setSelectedSku('');
                            }}
                          >
                            Remove
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="small text-muted">
                  No offer selected yet. Use Offer Picker above to add grouped store offers.
                </div>
              )}
              {unmatchedManualSkus.length > 0 ? (
                <div className="mt-2">
                  <div className="small font-weight-bold text-uppercase text-muted mb-1">Manual SKU Fallback</div>
                  {unmatchedManualSkus.map((sku) => (
                    <div key={sku} className="small text-monospace" style={{ wordBreak: 'break-all' }}>
                      {sku}
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
            <details className="mt-2">
              <summary className="small text-muted" style={{ cursor: 'pointer', outline: 'none' }}>
                Advanced raw SKU input
              </summary>
              <div className="mt-2">
                <Form.Control
                  as="textarea"
                  rows={isCompact ? 4 : 5}
                  value={bulkSkusText}
                  disabled={configModalMode === 'groupEdit'}
                  onChange={(e) => setBulkSkusText(e.target.value)}
                  placeholder="com.freshplanet.sp3.ios.bundle.offer1\ncom.freshplanet.sp3.android.bundle.offer1"
                />
              </div>
            </details>
          </Col>
        </Row>
        {offerOptions.length === 0 && (
          <Form.Text muted>Offer options are unavailable from backend. You can still input SKU IDs manually.</Form.Text>
        )}
      </Modal.Body>
      <Modal.Footer style={{ padding: isCompact ? '12px 16px' : undefined }}>
        <Button variant="outline-secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant={configModalMode === 'groupAdd' ? 'success' : 'primary'}
          style={
            configModalMode === 'groupAdd'
              ? { backgroundColor: '#22b573', borderColor: '#22b573' }
              : { backgroundColor: '#3f7ee8', borderColor: '#3f7ee8' }
          }
          onClick={onSave}
          disabled={!hasValidConfigAmount || upsertConfigLoading}
        >
          {upsertConfigLoading
            ? 'Saving...'
            : configModalMode === 'groupAdd'
              ? 'Add SKU'
              : configModalMode === 'groupEdit'
                ? 'Save Points'
                : 'Save Config'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ConfigModal;
